import { CheckCircle, ErrorRoundedIcon } from '@/components/icons'
import { useState } from 'react'
import { CMButton } from './ui'

interface ClubResult {
  clubId: number
  clubName: string
  status: 'SUCCESS' | 'FAILURE'
  matchesUploaded?: number
  errors?: string[]
}

interface UploadResult {
  status: 'SUCCESS' | 'FAILURE'
  message?: string
  results?: {
    successCount: number
    failureCount: number
    clubResults: ClubResult[]
  }
  errors?: any
}

interface Props {
  result: UploadResult
  onClose: () => void
  onReUpload: () => void
}

export const BulkUploadResults = ({ result, onClose, onReUpload }: Props) => {
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set())

  const toggleErrorExpansion = (clubId: number) => {
    const newExpanded = new Set(expandedErrors)
    if (newExpanded.has(clubId)) {
      newExpanded.delete(clubId)
    } else {
      newExpanded.add(clubId)
    }
    setExpandedErrors(newExpanded)
  }

  const successClubs = result.results?.clubResults?.filter(club => club.status === 'SUCCESS') || []
  const failedClubs = result.results?.clubResults?.filter(club => club.status === 'FAILURE') || []
  const totalMatches = successClubs.reduce((sum, club) => sum + (club.matchesUploaded || 0), 0)

  return (
    <div className="flex flex-col gap-y-4">
      {/* Summary */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 border-l-4 border-gray-400">
        {result.status === 'SUCCESS' ? (
          <CheckCircle className="w-8 h-8 text-green-600" />
        ) : (
          <ErrorRoundedIcon className="w-8 h-8 text-red-600" />
        )}
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-navy-900">
            {result.status === 'SUCCESS' ? 'Upload Completed' : 'Upload Failed'}
          </h4>
          <div className="text-sm text-navy-900 text-opacity-70 space-y-1">
            {successClubs.length > 0 && (
              <p className="text-green-600">
                ✓ {successClubs.length} club{successClubs.length !== 1 ? 's' : ''} • {totalMatches} matches uploaded successfully
              </p>
            )}
            {failedClubs.length > 0 && (
              <p className="text-red-600">
                ✗ {failedClubs.length} club{failedClubs.length !== 1 ? 's' : ''} failed
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Results */}
      <div className="max-h-96 overflow-y-auto">
        {/* Successful uploads */}
        {successClubs.length > 0 && (
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-green-600 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Successful Uploads ({successClubs.length})
            </h5>
            <div className="space-y-2">
              {successClubs.map((club) => (
                <div key={club.clubId} className="flex items-center justify-between p-3 bg-green-50 border border-green-200">
                  <div>
                    <p className="text-sm font-medium text-navy-900">{club.clubName}</p>
                    <p className="text-xs text-navy-900 text-opacity-60">Club ID: {club.clubId}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-600">
                      {club.matchesUploaded || 0} matches
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Failed uploads */}
        {failedClubs.length > 0 && (
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-2">
              <ErrorRoundedIcon className="w-4 h-4" />
              Failed Uploads ({failedClubs.length})
            </h5>
            <div className="space-y-2">
              {failedClubs.map((club) => (
                <div key={club.clubId} className="border border-red-200">
                  <div className="flex items-center justify-between p-3 bg-red-50">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-navy-900">{club.clubName}</p>
                      <p className="text-xs text-navy-900 text-opacity-60">Club ID: {club.clubId}</p>
                    </div>
                    <button
                      onClick={() => toggleErrorExpansion(club.clubId)}
                      className="text-sm text-red-600 hover:text-red-700 underline"
                    >
                      {expandedErrors.has(club.clubId) ? 'Hide' : 'Show'} Errors
                    </button>
                  </div>
                  
                  {expandedErrors.has(club.clubId) && club.errors && (
                    <div className="p-3 bg-red-25 border-t border-red-200">
                      <div className="space-y-1">
                        {club.errors.map((error, index) => (
                          <p key={index} className="text-xs text-red-700 font-mono">
                            • {error}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General errors (fallback) */}
        {result.status === 'FAILURE' && result.errors && (
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-2">
              <ErrorRoundedIcon className="w-4 h-4" />
              Upload Errors
            </h5>
            <div className="p-3 bg-red-50 border border-red-200">
              <div className="space-y-1">
                {Array.isArray(result.errors) ? (
                  result.errors.map((error: any, index: number) => (
                    <p key={index} className="text-xs text-red-700">
                      • {typeof error === 'object' && error.message ? error.message : (typeof error === 'string' ? error : JSON.stringify(error))}
                    </p>
                  ))
                ) : (
                  <p className="text-xs text-red-700">
                    {result.message || 'An unexpected error occurred'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <CMButton variant="ghost" size="md" onClick={onReUpload} className="underline">
          Upload Different File
        </CMButton>
        <div className="flex gap-3">
          <CMButton variant="muted" size="md" onClick={onClose}>
            {result.status === 'SUCCESS' ? 'Done' : 'Close'}
          </CMButton>
          {failedClubs.length > 0 && (
            <CMButton
              variant="danger"
              size="md"
              onClick={() => {
                // Export failed records for retry
                const failedData = failedClubs.map(club => ({
                  clubId: club.clubId,
                  clubName: club.clubName,
                  errors: club.errors?.join('; ')
                }))
                const csvContent = 'data:text/csv;charset=utf-8,' + 
                  ['Club ID,Club Name,Errors'].concat(
                    failedData.map(row => `${row.clubId},"${row.clubName}","${row.errors}"`)
                  ).join('\n')
                const link = document.createElement('a')
                link.setAttribute('href', encodeURI(csvContent))
                link.setAttribute('download', 'failed_uploads.csv')
                link.click()
              }}
            >
              Export Failed Records
            </CMButton>
          )}
        </div>
      </div>
    </div>
  )
}